import logError from 'src/data/services/logError';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { Keyboard, Platform } from 'react-native';
import showAlert from 'src/core/utils/showAlert';
import errorHandler from 'src/core/utils/errorHandler';
import _ from 'lodash';
import { ApiService, SecureStorageService } from 'src/data/services/Services';
import Translations from 'src/core/utils/Translations';
import { authEventBus } from 'src/core/events/Events';

import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useNotificationStore } from 'src/domain/stores/useNotificationStore';
import { useFormStore } from 'src/domain/stores/useFormStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import Constants from 'src/core/utils/Constants';
import type { ApiUser } from 'src/core/models/api/ApiUser';
import { isApiError } from 'src/core/models/api/ApiError';

/**
 * The sentence the API sends with a rejected request, or null when it sent none.
 *
 * Every call below has two ways to fail: the API answers 200 with `code: 400` in the body, which
 * lands in the resolved branch, or it answers HTTP 400 and axios rejects. Both carry the same body,
 * but only the resolved branch read it -- so the same refusal reached the user as "Ongeldige inlog
 * gegevens" one way and "probeer het later nog eens" the other, which sends people looking for a
 * problem that is not there. Field errors arrive as a map and are flattened the way the resolved
 * branches already flatten them.
 */
function serverMessage(error: unknown): string | null {
	if (!isApiError(error)) return null;

	const data = error.response?.data?.data;

	if (typeof data === 'string') return data === '' ? null : data;

	if (data !== null && typeof data === 'object') {
		const flattened = Object.values(data as Record<string, string>)
			.toString()
			.replace(',', '\n');

		return flattened === '' ? null : flattened;
	}

	return null;
}

/**
 * Warns when the browser accepted the login but refused to keep the token.
 *
 * setAuthToken reads its own cookie back, so it knows. Nobody asked: the user was marked logged in
 * and found out at the next page load, when the session was simply gone. Safari blocks storage in a
 * cross-origin iframe outright and Firefox partitions it, so the drawer on topicusevents.nl/app is
 * exactly where this happens -- and there the way out is to open the app in a tab of its own.
 *
 * The session still works for as long as the page stays open: the token is in memory either way.
 */
function warnIfSessionIsNotStored(stored: boolean): void {
	if (stored || Platform.OS !== 'web' || typeof window === 'undefined') return;

	const embedded = window.parent !== window;

	showAlert({
		title: Translations.noticeSessionNotStoredTitle,
		message: embedded ? Translations.noticeSessionNotStoredEmbedded : Translations.noticeSessionNotStored,
	});
}

interface AuthenticationState {
	isLoggedin: boolean | null;
	statusCode: number | null;
	responseMessage: string;
	userToken: string;
	initialRoute: string;
	loginSuccess: boolean;
	proceedToApp: boolean;
	userProfile: ApiUser | null;
	isLoading: boolean;
	isDeletingAccount: boolean;
}

const initialState: AuthenticationState = {
	isLoggedin: null,
	statusCode: null,
	responseMessage: '',
	userToken: '',
	initialRoute: 'Login',
	loginSuccess: false,
	proceedToApp: false,
	userProfile: null,
	isLoading: false,
	isDeletingAccount: false,
};

// Registered once: init() can run more than once, and re-binding the 401 handler each time would accumulate
// listeners (multiple logouts per 401).
let unauthorizedListenerRegistered = false;

export const useAuthenticationStore = create(
	combine(initialState, (set, get) => ({
		init: async () => {
			const token = await SecureStorageService.getAuthToken();
			if (token) {
				// Restore the session: the token is persisted in SecureStorage but the store boots empty.
				set({ initialRoute: 'Index', userToken: token, isLoggedin: true });
				await useAuthenticationStore.getState().getProfile();
			} else {
				set({ initialRoute: 'Login', isLoggedin: false });
			}

			if (!unauthorizedListenerRegistered) {
				unauthorizedListenerRegistered = true;
				authEventBus.on(Constants.eventAuthOnUnauthorized, () => {
					// Say it out loud. A 401 clears the session, and without a word the app simply stops
					// being logged in: on web that is a tab someone left open, where nothing restarted and
					// nothing explains why the events are suddenly gone.
					const wasLoggedIn = get().isLoggedin === true;

					useAuthenticationStore
						.getState()
						.logout()
						.then(() => {
							if (wasLoggedIn) {
								showAlert({
									title: Translations.errorSessionExpiredTitle,
									message: Translations.errorSessionExpired,
								});
							}
						})
						.catch(logError);
				});
			}
		},

		login: async (email: string, password: string) => {
			set({ isLoading: true });
			try {
				const pushToken = useNotificationStore.getState().token;
				const formParams = useFormStore.getState().formParams;

				const { data } = await ApiService.endpoints.auth.login({ email, password, pushToken });
				const statusCode = data.code ?? 200;
				set({ statusCode });

				if (statusCode === 400) {
					return { success: false, message: data.data };
				}

				if (data.token !== undefined) {
					warnIfSessionIsNotStored(await SecureStorageService.setAuthToken(data.token));
					// Mark logged in right away so the caller can navigate immediately.
					set({ userToken: data.token, isLoggedin: true, statusCode: null });

					// Link pre-login local events to the account + refresh the profile/events in the BACKGROUND
					// (fire-and-forget).
					useAuthenticationStore.getState().postEvents(data.token).catch(logError);

					// pendingEventCode (from an event deeplink) is handled after navigating to Home
					// (usePendingEventDeeplink), so the "Evenement toevoegen?" confirmation appears after login.
					return { success: true, routeName: formParams ? 'Custom' : 'Index' };
				}

				set({ isLoggedin: true, statusCode: null });
				return { success: true, routeName: formParams ? 'Custom' : 'Index' };
			} catch (error) {
				const status = isApiError(error) ? error.response?.status : undefined;
				const message =
					serverMessage(error) ??
					(status === 400 || status === 401 ? Translations.errorInvalidCredentials : Translations.errorDefaultMessage);
				return { success: false, message };
			} finally {
				set({ isLoading: false });
			}
		},

		logout: async () => {
			await SecureStorageService.clearAuthToken();
			// Clear the locally stored events: logging out detaches the local cache from the account.
			useEventStore.getState().clearStoredEvents();
			set({ isLoggedin: false, userToken: '', userProfile: null });
		},

		deleteAccount: async (): Promise<boolean> => {
			set({ isDeletingAccount: true });
			try {
				// Hard delete server-side, then clear the local session/events.
				await ApiService.endpoints.user.deleteAccount();
				await useAuthenticationStore.getState().logout();
				return true;
			} catch (error) {
				console.error(error);
				return false;
			} finally {
				set({ isDeletingAccount: false });
			}
		},

		postEvents: async (token: string) => {
			if (!token) return;
			const events = useEventStore.getState().eventData.map((e: { uuid: string }) => e.uuid);

			try {
				await ApiService.endpoints.user.addEvents({ events });
				await useAuthenticationStore.getState().getProfile();
			} catch (error) {
				console.error(error);
			}
		},

		deleteEvents: async (event: string | string[], token: string) => {
			if (!token) return;
			const events = Array.isArray(event) ? event : [event];
			try {
				await ApiService.endpoints.user.deleteEvents({ events });
				await useAuthenticationStore.getState().getProfile();
			} catch (error) {
				console.error(error);
			}
		},

		register: async (email: string, password: string, firstname: string, lastname: string) => {
			set({ isLoading: true });
			try {
				const { data } = await ApiService.endpoints.auth.register({ email, password, firstname, lastname });
				const statusCode = data.code ?? 200;

				if (statusCode === 400) {
					return { success: false, message: data.data };
				}

				if (data.status !== 'success') {
					throw new Error(data.data ?? 'Er ging iets fout, probeer het later opnieuw');
				}

				if (data.token) {
					warnIfSessionIsNotStored(await SecureStorageService.setAuthToken(data.token));
				}
				set({ userToken: data.token ?? '', isLoggedin: true });
				await useAuthenticationStore.getState().getProfile();

				// pendingEventCode (from an event deeplink) is handled after navigating to Home
				// (usePendingEventDeeplink), so the "Evenement toevoegen?" confirmation appears after login.

				// formParams is set when the user arrived via a form flow; route to the form-registration completion in
				// that case (mirrors login()'s truthy check).
				const formParams = useFormStore.getState().formParams;
				return {
					success: true,
					routeName: formParams ? 'formRegistration' : 'registration',
				};
			} catch (error) {
				// Surface the backend message from the 400 body (e.g.
				const backendMessage = (error as { response?: { data?: { data?: string } } })?.response?.data?.data;
				const message = backendMessage ?? Translations.errorDefaultMessage;
				return { success: false, message };
			} finally {
				set({ isLoading: false });
			}
		},

		forgotPassword: async (email: string) => {
			set({ isLoading: true });
			try {
				const { data } = await ApiService.endpoints.auth.forgotPassword({ email });
				if (data.status === 'success') {
					useAuthenticationStore
						.getState()
						.handleSuccessResponse(Translations.successPasswordForgotSubject, Translations.successPasswordForgotMessage);
					return true;
				} else if (data.data === 'provide an email adres') {
					errorHandler(Translations.errorEmptyEmailSubject, Translations.errorEmptyEmailMessage);
				} else {
					errorHandler(Translations.errorUserNotFoundSubject, Translations.errorUserNotFoundMessage);
				}
			} catch {
				errorHandler(Translations.errorDefaultSubject, Translations.errorDefaultMessage);
			} finally {
				set({ isLoading: false });
			}
			return false;
		},

		getProfile: async () => {
			const { userToken } = get();
			if (_.isEmpty(userToken)) return;

			const pushToken = useNotificationStore.getState().token;

			try {
				const { data } = await ApiService.endpoints.user.getProfile({ pushToken });
				if (data.id) {
					set({ userProfile: data });
					// Awaited so callers know the account's events are synced locally once getProfile resolves.
					await useEventStore.getState().updateEvents(data.events).catch(logError);
				}
			} catch (error) {
				console.error(error);
				// Only log out on a real auth error (401).
				const status = (error as { response?: { status?: number } })?.response?.status;
				if (status === 401) {
					void useAuthenticationStore.getState().logout();
				}
			}
		},

		updateUser: async (profileImage: string, email: string, password: string, voornaam: string, achternaam: string) => {
			set({ isLoading: true });
			const profileObj = password.length
				? { email, wachtwoord: password, voornaam, achternaam, base64: profileImage }
				: { email, base64: profileImage, voornaam, achternaam };

			try {
				const { data } = await ApiService.endpoints.user.updateProfile(profileObj);
				if (data.status === 'success') {
					// A password change rotates the auth token server-side.
					let nextToken = data.token;
					if (!nextToken && password.length) {
						try {
							const pushToken = useNotificationStore.getState().token;
							const { data: relog } = await ApiService.endpoints.auth.login({ email, password, pushToken });
							nextToken = relog.token;
						} catch (reauthError) {
							console.error('Re-auth after password change failed:', reauthError);
						}
					}
					if (nextToken) {
						await SecureStorageService.setAuthToken(nextToken);
						set({ userToken: nextToken });
					}
					await useAuthenticationStore.getState().getProfile();
					useAuthenticationStore
						.getState()
						.handleSuccessResponse(Translations.successEditProfileSubject, Translations.successEditProfileMessage);
				} else if (data.code === 400) {
					// Recoverable validation error (e.g.
					const message =
						typeof data.data === 'object'
							? Object.values(data.data as Record<string, string>)
									.toString()
									.replace(',', '\n')
							: (data.data as string | undefined);
					set({ responseMessage: message ?? '' });
					useAuthenticationStore.getState().handleErrorResponse(message ?? '');
				}
			} catch (error) {
				const message = serverMessage(error);

				if (message === null) {
					logError(error);
				} else {
					set({ responseMessage: message });
					useAuthenticationStore.getState().handleErrorResponse(message);
				}
			} finally {
				set({ isLoading: false });
			}
		},

		resetPassword: async (token: string, password: string) => {
			set({ isLoading: true });
			try {
				const { data } = await ApiService.endpoints.auth.resetPassword({ token, password });
				set({ statusCode: data.code ?? null });

				if (data.status === 'success') {
					errorHandler(Translations.successPasswordChanged, null);
				}

				if (data.code === 400) {
					const message =
						typeof data.data === 'object'
							? Object.values(data.data as Record<string, string>)
									.toString()
									.replace(',', '\n')
							: (data.data as string | undefined);
					set({ responseMessage: message ?? '' });
					useAuthenticationStore.getState().handleErrorResponse(message ?? '');
				}
			} catch (error) {
				const message = serverMessage(error);

				if (message === null) {
					logError(error);
				} else {
					set({ responseMessage: message });
					useAuthenticationStore.getState().handleErrorResponse(message);
				}
			} finally {
				set({ isLoading: false });
			}
		},

		handleErrorResponse: (message: string) => {
			const { responseMessage } = get();
			showAlert({
				title: Translations.errorDefaultSubject,
				message: message || responseMessage,
				confirmText: Translations.errorTryAgain,
				onConfirm: () => set({ responseMessage: '' }),
			});
		},

		handleSuccessResponse: (subject: string, message: string, action?: () => void) => {
			showAlert({ title: subject, message, onConfirm: action, cancelable: false });
		},

		proceedTo: (routeName: string) => {
			Keyboard.dismiss();
			if (routeName === Constants.screenCustom) {
				useNavigationStore.getState().navigate(Constants.screenFormLogin);
			} else {
				useNavigationStore.getState().navigate(routeName);
			}
		},

		resetTo: (routeName: string) => {
			Keyboard.dismiss();
			setTimeout(() => {
				useNavigationStore.getState().navigate(routeName);
			}, 100);
		},
	})),
);

// Account sync for local event-list changes. useEventStore raises these instead of calling the store directly, so
// it does not have to import this module (require cycle). Both actions are no-ops without a user token.
authEventBus.on(Constants.eventEventsAdded, () => {
	const { userToken, postEvents } = useAuthenticationStore.getState();
	postEvents(userToken).catch(logError);
});

authEventBus.on(Constants.eventEventsRemoved, (event) => {
	const { userToken, deleteEvents } = useAuthenticationStore.getState();
	deleteEvents(event as string | string[], userToken).catch(logError);
});
